import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import products from "../data/products.json";
import { useNotification } from "./NotificationContext";
import { auth, db, googleProvider } from "../firebase";
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
} from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";

const UserContext = createContext(null);

const LOCAL_STORAGE_KEY = "ecobazar-current-user";
const LEGACY_USERS_COLLECTION = "legacyUsers";
const USERS_COLLECTION = "users";

function createEmptyUserProfile(overrides = {}) {
  return {
    id: "",
    pfp: "/users/default-user.png",
    name: "",
    email: "",
    billingAddress: {
      firstName: "",
      lastName: "",
      companyName: "",
      address: "",
      country: "",
      city: "",
      zipCode: "",
      email: "",
      phone: "",
    },
    cart: [],
    cartCoupon: "",
    usedCoupons: [],
    wishlist: [],
    todos: [],
    orderHistory: [],
    authProvider: "password",
    legacyId: null,
    ...overrides,
  };
}

function sanitizeLegacyUser(user) {
  if (!user) {
    return null;
  }

  const { password, isLoggedIn, ...safeUser } = user;

  return createEmptyUserProfile({
    ...safeUser,
    email: user.email?.trim().toLowerCase() || "",
    legacyId: user.id || null,
  });
}

async function findLegacyUserByEmail(email) {
  const normalizedEmail = email.trim().toLowerCase();
  const legacyQuery = query(
    collection(db, LEGACY_USERS_COLLECTION),
    where("email", "==", normalizedEmail),
    limit(1),
  );
  const snapshot = await getDocs(legacyQuery);

  if (snapshot.empty) {
    return null;
  }

  const legacyDocument = snapshot.docs[0];
  return {
    id: legacyDocument.id,
    ...legacyDocument.data(),
  };
}

function normalizeProfile(profile, fallbackAuthUser = null) {
  if (!profile) {
    return null;
  }

  return createEmptyUserProfile({
    ...profile,
    id: profile.id || fallbackAuthUser?.uid || "",
    email:
      profile.email?.trim().toLowerCase() ||
      fallbackAuthUser?.email?.trim().toLowerCase() ||
      "",
    name: profile.name || fallbackAuthUser?.displayName || "",
    pfp: profile.pfp || fallbackAuthUser?.photoURL || "/users/default-user.png",
  });
}

function formatFirebaseError(error, fallbackMessage) {
  if (!error) {
    return fallbackMessage;
  }

  const code = error.code ? ` (${error.code})` : "";
  const message = error.message ? ` ${error.message}` : "";
  return `${fallbackMessage}${code}${message}`;
}

async function ensureUserProfile(authUser) {
  if (!authUser) {
    return null;
  }

  const userRef = doc(db, USERS_COLLECTION, authUser.uid);
  const userSnapshot = await getDoc(userRef);

  if (userSnapshot.exists()) {
    return normalizeProfile(userSnapshot.data(), authUser);
  }

  const legacyUser = await findLegacyUserByEmail(authUser.email || "");
  const providerId =
    authUser.providerData?.[0]?.providerId || GoogleAuthProvider.PROVIDER_ID;

  const mergedProfile = normalizeProfile(
    legacyUser
      ? {
          ...sanitizeLegacyUser(legacyUser),
          id: authUser.uid,
          authProvider:
            providerId === GoogleAuthProvider.PROVIDER_ID ? "google" : "password",
          legacyId: legacyUser.id,
        }
      : {
          id: authUser.uid,
          name: authUser.displayName || "",
          email: authUser.email || "",
          pfp: authUser.photoURL || "/users/default-user.png",
          authProvider:
            providerId === GoogleAuthProvider.PROVIDER_ID ? "google" : "password",
        },
    authUser,
  );

  await setDoc(userRef, {
    ...mergedProfile,
    updatedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
  });

  if (legacyUser) {
    await updateDoc(doc(db, LEGACY_USERS_COLLECTION, String(legacyUser.id)), {
      migratedToAuth: true,
      migratedUid: authUser.uid,
      updatedAt: serverTimestamp(),
    });
  }

  return mergedProfile;
}

export function UserProvider({ children }) {
  const { notify } = useNotification();
  const storedUser =
    typeof window !== "undefined"
      ? JSON.parse(window.localStorage.getItem(LOCAL_STORAGE_KEY) || "null")
      : null;

  const [currentUser, setCurrentUser] = useState(storedUser);
  const [firebaseUser, setFirebaseUser] = useState(auth.currentUser);
  const [isLoadingUser, setIsLoadingUser] = useState(true);

  useEffect(() => {
    let unsubscribe = () => {};
    let isMounted = true;

    async function initializeUserState() {
      unsubscribe = onAuthStateChanged(auth, async (authUser) => {
        if (!isMounted) {
          return;
        }

        setFirebaseUser(authUser);

        if (!authUser) {
          setCurrentUser(null);
          window.localStorage.removeItem(LOCAL_STORAGE_KEY);
          setIsLoadingUser(false);
          return;
        }

        const providerId =
          authUser.providerData?.[0]?.providerId || GoogleAuthProvider.PROVIDER_ID;
        const fallbackProfile = normalizeProfile(
          {
            id: authUser.uid,
            name: authUser.displayName || "",
            email: authUser.email || "",
            pfp: authUser.photoURL || "/users/default-user.png",
            authProvider:
              providerId === GoogleAuthProvider.PROVIDER_ID ? "google" : "password",
          },
          authUser,
        );

        setCurrentUser((previousUser) => previousUser || fallbackProfile);
        window.localStorage.setItem(
          LOCAL_STORAGE_KEY,
          JSON.stringify(fallbackProfile),
        );

        try {
          const profile = await ensureUserProfile(authUser);

          if (!isMounted) {
            return;
          }

          setCurrentUser(profile);
          window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(profile));
        } catch (error) {
          console.error("Failed to load Firebase user profile.", error);
          setCurrentUser(fallbackProfile);
          notify(
            formatFirebaseError(
              error,
              "Signed in, but your Firestore profile could not be fully loaded.",
            ),
            "error",
          );
        } finally {
          if (isMounted) {
            setIsLoadingUser(false);
          }
        }
      });
    }

    initializeUserState();

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [notify]);

  const cartTotal = useMemo(() => {
    if (!currentUser?.cart?.length) {
      return 0;
    }

    return currentUser.cart.reduce((total, cartItem) => {
      const product = products.find((item) => item.id === cartItem.productId);

      if (!product) {
        return total;
      }

      return total + product.price * cartItem.quantity;
    }, 0);
  }, [currentUser]);

  function saveLocalUser(nextUser) {
    if (nextUser) {
      window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(nextUser));
    } else {
      window.localStorage.removeItem(LOCAL_STORAGE_KEY);
    }
  }

  async function persistUser(nextUser) {
    setCurrentUser(nextUser);
    saveLocalUser(nextUser);

    try {
      await setDoc(
        doc(db, USERS_COLLECTION, String(nextUser.id)),
        {
          ...nextUser,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    } catch (error) {
      console.error("Failed to persist user changes to Firebase.", error);
      notify(
        formatFirebaseError(error, "Failed to save your profile to Firestore."),
        "error",
      );
      throw error;
    }
  }

  async function signIn({ email, password }) {
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail || !password.trim()) {
      notify("Please enter both email and password.", "error");
      return { success: false };
    }

    try {
      await signInWithEmailAndPassword(auth, normalizedEmail, password);
      notify("Signed in successfully.");
      return { success: true };
    } catch (error) {
      console.error("Failed to sign in with Firebase.", error);
      notify(formatFirebaseError(error, "Invalid email or password."), "error");
      return { success: false };
    }
  }

  async function signUp({ name, email, password, confirmPassword }) {
    const trimmedName = name.trim();
    const normalizedEmail = email.trim().toLowerCase();

    if (!trimmedName || !normalizedEmail || !password.trim() || !confirmPassword.trim()) {
      notify("Please complete all fields.", "error");
      return { success: false };
    }

    if (password !== confirmPassword) {
      notify("Passwords do not match.", "error");
      return { success: false };
    }

    try {
      const credential = await createUserWithEmailAndPassword(
        auth,
        normalizedEmail,
        password,
      );

      await updateProfile(credential.user, {
        displayName: trimmedName,
      });

      const newUser = {
        ...createEmptyUserProfile(),
        id: credential.user.uid,
        pfp: "/users/default-user.png",
        name: trimmedName,
        email: normalizedEmail,
        authProvider: "password",
      };

      await persistUser(newUser);
      notify("Account created successfully.");
      return { success: true };
    } catch (error) {
      console.error("Firebase sign up failed.", error);
      notify(
        formatFirebaseError(
          error,
          "Could not create account. This email may already be in use.",
        ),
        "error",
      );
      return { success: false };
    }
  }

  async function signInWithGoogle() {
    try {
      await signInWithPopup(auth, googleProvider);
      notify("Signed in with Google successfully.");
      return { success: true };
    } catch (error) {
      console.error("Google sign-in failed.", error);

      if (error.code === "auth/account-exists-with-different-credential") {
        const conflictingEmail = error.customData?.email;

        if (conflictingEmail) {
          try {
            const signInMethods = await fetchSignInMethodsForEmail(
              auth,
              conflictingEmail,
            );

            if (signInMethods.includes("password")) {
              notify(
                "An account with this email already exists. Sign in with email and password first, then use the same email to keep your migrated profile data.",
                "error",
              );
              return { success: false };
            }
          } catch (methodsError) {
            console.error("Could not check existing sign-in methods.", methodsError);
          }
        }
      }

      notify(formatFirebaseError(error, "Google sign-in failed."), "error");
      return { success: false };
    }
  }

  async function addToCart(product) {
    if (!currentUser || !product) {
      return;
    }

    const existingCart = currentUser.cart || [];
    const existingItem = existingCart.find(
      (cartItem) => cartItem.productId === product.id,
    );

    const updatedCart = existingItem
      ? existingCart.map((cartItem) =>
          cartItem.productId === product.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem,
        )
      : [...existingCart, { productId: product.id, quantity: 1 }];

    notify(`${product.name} added to cart.`);

    await persistUser({
      ...currentUser,
      cart: updatedCart,
    });
  }

  async function toggleWishlist(product) {
    if (!currentUser || !product) {
      return;
    }

    const existingWishlist = currentUser.wishlist || [];
    const isWishlisted = existingWishlist.includes(product.id);

    const updatedWishlist = isWishlisted
      ? existingWishlist.filter((productId) => productId !== product.id)
      : [...existingWishlist, product.id];

    notify(
      isWishlisted
        ? `${product.name} removed from wishlist.`
        : `${product.name} added to wishlist.`,
    );

    await persistUser({
      ...currentUser,
      wishlist: updatedWishlist,
    });
  }

  async function updateCartQuantity(productId, quantity) {
    if (!currentUser) {
      return;
    }

    const normalizedQuantity = Math.max(1, quantity);
    const updatedCart = (currentUser.cart || []).map((cartItem) =>
      cartItem.productId === productId
        ? { ...cartItem, quantity: normalizedQuantity }
        : cartItem,
    );

    const updatedItem = updatedCart.find(
      (cartItem) => cartItem.productId === productId,
    );
    const product = products.find((item) => item.id === productId);

    if (updatedItem && product) {
      notify(`${product.name} quantity updated to ${updatedItem.quantity}.`);
    }

    await persistUser({
      ...currentUser,
      cart: updatedCart,
    });
  }

  async function removeFromCart(productId) {
    if (!currentUser) {
      return;
    }

    const updatedCart = (currentUser.cart || []).filter(
      (cartItem) => cartItem.productId !== productId,
    );

    const product = products.find((item) => item.id === productId);

    if (product) {
      notify(`${product.name} removed from cart.`);
    }

    await persistUser({
      ...currentUser,
      cart: updatedCart,
    });
  }

  async function replaceCart(updatedCart) {
    if (!currentUser) {
      return;
    }

    notify("Cart updated.");

    await persistUser({
      ...currentUser,
      cart: updatedCart,
    });
  }

  async function updateUserDetails(updates, successMessage = "Profile updated.") {
    if (!currentUser) {
      return { success: false };
    }

    const nextUser = {
      ...currentUser,
      ...updates,
    };

    notify(successMessage);
    await persistUser(nextUser);
    return { success: true };
  }

  async function placeOrder(orderPayload) {
    if (!currentUser?.cart?.length) {
      notify("Your cart is empty.", "error");
      return { success: false };
    }

    const nextOrderId = Date.now();
    const nextOrder = {
      id: nextOrderId,
      date: new Date().toISOString().slice(0, 10),
      status: "Processing",
      items: currentUser.cart,
      total: orderPayload.total,
      paymentMethod: orderPayload.paymentMethod,
      billingAddress: orderPayload.billingAddress,
      shippingAddress: orderPayload.shippingAddress,
      orderNotes: orderPayload.orderNotes || "",
      shipping: orderPayload.shipping,
      subtotal: orderPayload.subtotal,
      discountRate: 0,
    };

    const nextUser = {
      ...currentUser,
      billingAddress: orderPayload.billingAddress,
      cart: [],
      cartCoupon: "",
      orderHistory: [nextOrder, ...(currentUser.orderHistory || [])],
    };

    notify("Order placed successfully.");
    await persistUser(nextUser);
    return { success: true, order: nextOrder };
  }

  async function applyCoupon(code) {
    if (!currentUser) {
      return { success: false, message: "No active user found." };
    }

    const normalizedCode = code.trim().toUpperCase();

    if (!normalizedCode) {
      notify("Please enter a coupon code.", "error");
      return { success: false, message: "Please enter a coupon code." };
    }

    if (normalizedCode !== "FREESHIP") {
      notify("Invalid coupon code.", "error");
      return { success: false, message: "Invalid coupon code." };
    }

    if (currentUser.usedCoupons?.includes(normalizedCode)) {
      notify(`${normalizedCode} has already been used.`, "error");
      return { success: false, message: `${normalizedCode} has already been used.` };
    }

    const nextUser = {
      ...currentUser,
      cartCoupon: normalizedCode,
      usedCoupons: [...(currentUser.usedCoupons || []), normalizedCode],
    };

    notify(`${normalizedCode} applied. Shipping is now free.`);
    await persistUser(nextUser);
    return { success: true, message: `${normalizedCode} applied.` };
  }

  async function addTodo(title) {
    if (!currentUser) {
      notify("Please sign in to save tasks.", "error");
      return { success: false };
    }

    const trimmedTitle = title.trim();

    if (!trimmedTitle) {
      notify("Write a task before adding it.", "error");
      return { success: false };
    }

    const now = new Date().toISOString();
    const nextTodo = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      title: trimmedTitle,
      completed: false,
      createdAt: now,
      updatedAt: now,
    };

    await persistUser({
      ...currentUser,
      todos: [nextTodo, ...(currentUser.todos || [])],
    });

    notify("Task added.");
    return { success: true, todo: nextTodo };
  }

  async function toggleTodoCompletion(todoId) {
    if (!currentUser) {
      return { success: false };
    }

    const currentTodos = currentUser.todos || [];
    const targetTodo = currentTodos.find((todo) => todo.id === todoId);

    if (!targetTodo) {
      return { success: false };
    }

    const nextTodos = currentTodos.map((todo) =>
      todo.id === todoId
        ? {
            ...todo,
            completed: !todo.completed,
            updatedAt: new Date().toISOString(),
          }
        : todo,
    );

    await persistUser({
      ...currentUser,
      todos: nextTodos,
    });

    return { success: true };
  }

  async function removeTodo(todoId) {
    if (!currentUser) {
      return { success: false };
    }

    const currentTodos = currentUser.todos || [];
    const nextTodos = currentTodos.filter((todo) => todo.id !== todoId);

    await persistUser({
      ...currentUser,
      todos: nextTodos,
    });

    notify("Task removed.");
    return { success: true };
  }

  async function logout() {
    try {
      await signOut(auth);
      setCurrentUser(null);
      saveLocalUser(null);
    } catch (error) {
      console.error("Failed to sign out from Firebase.", error);
      notify("Logout failed.", "error");
      return;
    }

    notify("Logged out successfully.");
  }

  const value = {
    currentUser,
    isAuthenticated: Boolean(firebaseUser),
    isLoadingUser,
    cartTotal,
    addToCart,
    toggleWishlist,
    updateCartQuantity,
    removeFromCart,
    replaceCart,
    updateUserDetails,
    placeOrder,
    applyCoupon,
    addTodo,
    toggleTodoCompletion,
    removeTodo,
    signIn,
    signUp,
    signInWithGoogle,
    logout,
    isInWishlist: (productId) =>
      Boolean(currentUser?.wishlist?.includes(productId)),
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error("useUser must be used within a UserProvider.");
  }

  return context;
}
